import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  clearAuth,
  getProfile,
  getPositions,
  resolveWsUrl,
  requestBalanceChange,
  getUserHistory,
  getStoredEmail,
  getToken,
} from "../api";
import { ThemeContext } from "../App";

const TABS = [
  { id: "quotes", label: "Quotes", icon: "📈" },
  { id: "trade", label: "Trade", icon: "💼" },
  { id: "profile", label: "Profile", icon: "👤" },
];

const SYMBOLS = [
  { symbol: "BTCUSD", label: "BTC/USD", display: "BTC/USD" },
  { symbol: "ETHUSD", label: "ETH/USD", display: "ETH/USD" },
  { symbol: "SOLUSD", label: "SOL/USD", display: "SOL/USD" },
  { symbol: "BNBUSD", label: "BNB/USD", display: "BNB/USD" },
  { symbol: "XAUUSD", label: "XAU/USD", display: "XAU/USD" },
  { symbol: "SPX500USD", label: "SPX500/USD", display: "SPX500/USD" },  // Feature 3: S&P 500 Index
];

const REFRESH_INTERVAL_MS = 10_000;
// Lot multiplier for XAUUSD: 0.01 lot = 1 oz of gold
const XAUUSD_LOT_MULTIPLIER = 100;
// Crypto uses direct quantity (no lot conversion)
const CRYPTO_DIRECT_QTY_SYMBOLS = ["BTCUSD", "ETHUSD", "SOLUSD", "BNBUSD"];
// All tradeable symbols
const ALL_SYMBOLS = ["BTCUSD", "ETHUSD", "SOLUSD", "BNBUSD", "XAUUSD", "SPX500USD"];
const CRYPTO_SYMBOLS = ALL_SYMBOLS; // Alias for backward compatibility
const OPTION_CONTRACT_MULTIPLIER = 100; // Options contract size

// Get lot multiplier for a symbol (XAUUSD: 100, Crypto: 1)
const getLotMultiplier = (symbol) => {
  const normalized = (symbol || "").toUpperCase().replace(/[/_]/g, "");
  if (normalized === "XAUUSD") return XAUUSD_LOT_MULTIPLIER;
  return 1; // Crypto uses direct qty
};
const USDT_WALLET_ADDRESS = "0x13994fE5ca3eb1D33575A1A50021ce50132564AF";

const DEPOSIT_KEYWORDS = [
  "deposit",
  "topup",
  "top-up",
  "credit",
  "fund",
  "funding",
  "add",
  "added",
  "load",
  "initial",
  "opening",
  "start",
];

const WITHDRAWAL_KEYWORDS = [
  "withdraw",
  "withdrawal",
  "payout",
  "debit",
  "transfer out",
  "transfer-out",
  "deduct",
  "fee",
  "charge",
];

const HISTORY_FILTER_PRESETS = [
  { id: "1d", label: "1 Day" },
  { id: "7d", label: "7 Days" },
  { id: "15d", label: "15 Days" },
  { id: "30d", label: "30 Days" },
  { id: "custom", label: "Custom" },
];

export default function UserDashboard() {
  const [profile, setProfile] = useState({});
  const [positions, setPositions] = useState([]);
  const [prices, setPrices] = useState({});
  const [activeTab, setActiveTab] = useState("quotes");
  const [balanceModal, setBalanceModal] = useState({
    open: false,
    type: "add",
    amount: "",
  });
  const [balanceError, setBalanceError] = useState("");
  const [balanceNotice, setBalanceNotice] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false); // visible loader for first load / filter apply
  const [historyRefreshing, setHistoryRefreshing] = useState(false); // silent background refresh indicator
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [historyData, setHistoryData] = useState({
    transactions: [],
    closed_positions: [],
    summary: null,
  });
  const [historyLastUpdated, setHistoryLastUpdated] = useState(null);
  const [historyFilter, setHistoryFilter] = useState("7d");
  const [historyCustom, setHistoryCustom] = useState({ start: "", end: "" });
  const [historyFilterError, setHistoryFilterError] = useState("");
  const wsUrl = useMemo(() => resolveWsUrl(), []);
  const wsRef = useRef(null);
  const positionsWsRef = useRef(null);
  const positionsWsActiveRef = useRef(false);
  const navigate = useNavigate();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const storedUsername = useMemo(() => localStorage.getItem("username"), []);
  const storedEmail = useMemo(
    () => getStoredEmail?.() || localStorage.getItem("email"),
    []
  );
  const isMountedRef = useRef(true);
  const profileRequestRef = useRef(false);
  const positionsRequestRef = useRef(false);
  const historyRequestRef = useRef(false);
  const historyDebounceRef = useRef(null);

  const styles = useMemo(() => {
    const isDark = theme === "dark";
    return {
      viewport: isDark
        ? "bg-slate-900 text-white"
        : "bg-slate-100 text-gray-900",
      surface: isDark ? "bg-slate-950 text-white" : "bg-white text-gray-900",
      card: isDark
        ? "bg-slate-900 border border-slate-800 text-white"
        : "bg-gray-50 border border-gray-200 text-gray-900",
      muted: isDark ? "text-slate-300" : "text-gray-500",
      navActive: "text-emerald-500",
      navInactive: isDark ? "text-slate-400" : "text-gray-400",
      navBorder: isDark ? "border-slate-800" : "border-gray-200",
    };
  }, [theme]);

  const formatCurrency = useCallback(
    (value) =>
      Number(value || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    []
  );

  const handleCopyDepositAddress = useCallback(() => {
    navigator.clipboard?.writeText(USDT_WALLET_ADDRESS).catch(() => {});
  }, []);

  const classifyTransaction = useCallback((tx) => {
    if (!tx || typeof tx.amount !== "number") return "other";
    const amount = Number(tx.amount);
    const action = (tx.action || "").toLowerCase();
    const note = (tx.note || "").toLowerCase();
    const joined = `${action} ${note}`.trim();

    const matchesDeposit = DEPOSIT_KEYWORDS.some((keyword) =>
      joined.includes(keyword)
    );
    const matchesWithdraw = WITHDRAWAL_KEYWORDS.some((keyword) =>
      joined.includes(keyword)
    );

    if (matchesDeposit && !matchesWithdraw) return "deposit";
    if (matchesWithdraw && !matchesDeposit) return "withdraw";
    if (amount > 0) return "deposit";
    if (amount < 0) return "withdraw";
    return "other";
  }, []);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const balanceDisplay = useMemo(() => {
    return typeof profile.balance === "number"
      ? formatCurrency(profile.balance)
      : "--";
  }, [profile.balance, formatCurrency]);

  const totals = useMemo(() => {
    const pnl = positions.reduce(
      (acc, pos) => acc + (pos.unrealized_pnl || 0),
      0
    );
    const balance = typeof profile.balance === "number" ? profile.balance : 0;
    return {
      pnl,
      balance,
      equity: balance + pnl,
    };
  }, [positions, profile.balance]);

  const closedSummary = useMemo(() => {
    const summaryData = historyData?.summary;
    if (summaryData) {
      const depositsTotal = Number(summaryData.deposits_total ?? 0);
      const withdrawalsTotal = Number(summaryData.withdrawals_total ?? 0);
      const profit = Number(summaryData.closed_pnl_total ?? 0);
      const netCash = Number(
        summaryData.transactions_net ?? depositsTotal - withdrawalsTotal
      );
      const balance = netCash + profit;
      return {
        deposit: netCash,
        depositsTotal,
        withdrawalsTotal,
        profit,
        balance,
      };
    }

    const transactions = historyData?.transactions ?? [];
    const closedPositions = historyData?.closed_positions ?? [];

    const { depositsTotal, withdrawalsTotal } = transactions.reduce(
      (acc, tx) => {
        if (!tx || typeof tx.amount !== "number") return acc;
        const amountAbs = Math.abs(tx.amount);
        const classification = classifyTransaction(tx);

        if (classification === "deposit") {
          acc.depositsTotal += amountAbs;
        } else if (classification === "withdraw") {
          acc.withdrawalsTotal += amountAbs;
        }
        return acc;
      },
      { depositsTotal: 0, withdrawalsTotal: 0 }
    );

    const profit = closedPositions.reduce((acc, pos) => {
      if (!pos) return acc;
      const entry = Number(pos.entry_price ?? 0);
      const qty = Number(pos.qty ?? 0);
      const closedPrice = Number(
        pos.closed_price ?? pos.current_price ?? entry
      );
      const side = (pos.side || "buy").toLowerCase();
      const pnl =
        side === "sell"
          ? (entry - closedPrice) * qty
          : (closedPrice - entry) * qty;
      return acc + (Number.isFinite(pnl) ? pnl : 0);
    }, 0);

    const netDeposits = depositsTotal - withdrawalsTotal;

    return {
      deposit: netDeposits,
      depositsTotal,
      withdrawalsTotal,
      profit,
      balance: netDeposits + profit,
    };
  }, [historyData, classifyTransaction]);

  // Deprecated: superseded by inline summary; kept for reference. Not used.
  // eslint-disable-next-line no-unused-vars
  const renderAccountFundingCard = () => {
    const summaryReady = historyLoaded && !historyLoading;
    const depositsDisplay = summaryReady
      ? `$${formatCurrency(
          closedSummary.depositsTotal ?? closedSummary.deposit
        )}`
      : historyLoading
      ? "Loading..."
      : "--";
    const withdrawalsDisplay = summaryReady
      ? `$${formatCurrency(closedSummary.withdrawalsTotal || 0)}`
      : historyLoading
      ? "Loading..."
      : "--";
    const summaryBalanceDisplay = summaryReady
      ? `$${formatCurrency(closedSummary.balance)}`
      : historyLoading
      ? "Loading..."
      : "--";
    const realizedPlDisplay = summaryReady
      ? `$${formatCurrency(closedSummary.profit)}`
      : historyLoading
      ? "Loading..."
      : "--";

    const balanceClass = summaryReady
      ? closedSummary.balance >= 0
        ? "text-emerald-500"
        : "text-red-500"
      : styles.muted;
    const withdrawalClass = summaryReady
      ? closedSummary.withdrawalsTotal > 0
        ? "text-red-500"
        : styles.muted
      : styles.muted;
    const profitClass = summaryReady
      ? closedSummary.profit >= 0
        ? "text-emerald-500"
        : "text-red-500"
      : styles.muted;

    return (
      <div className={`rounded-2xl px-4 py-4 shadow-sm ${styles.card}`}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-semibold">Account Funding</p>
          {historyLoading && (
            <span className={`text-[11px] ${styles.muted}`}>Updating...</span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Deposits</span>
            <span className="text-lg font-semibold">{depositsDisplay}</span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Withdrawals</span>
            <span className={`text-lg font-semibold ${withdrawalClass}`}>
              {withdrawalsDisplay}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Balance</span>
            <span className={`text-lg font-semibold ${balanceClass}`}>
              {summaryBalanceDisplay}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Realized P/L</span>
            <span className={`text-lg font-semibold ${profitClass}`}>
              {realizedPlDisplay}
            </span>
          </div>
        </div>
        {!summaryReady && !historyLoading && (
          <p className={`text-[11px] text-center mt-3 ${styles.muted}`}>
            Totals refresh automatically when the History view loads.
          </p>
        )}
      </div>
    );
  };

  const getDecimals = useCallback((symbol) => {
    switch (symbol) {
      case "BTCUSD":
        return 2; // BTC typically 2 decimals
      case "ETHUSD":
        return 2; // ETH typically 2 decimals
      case "SOLUSD":
        return 2; // SOL typically 2 decimals
      default:
        return 2;
    }
  }, []);

  const formatPrice = useCallback(
    (symbol, value) => {
      if (typeof value !== "number" || Number.isNaN(value)) {
        return "--";
      }
      return value.toFixed(getDecimals(symbol));
    },
    [getDecimals]
  );
  const parseNumeric = useCallback((value) => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
  }, []);

  const formatQuantity = useCallback(
    (value) => {
      const numeric = parseNumeric(value);
      if (numeric === null) return "--";
      // For values >= 1, show max 2 decimals
      // For small values (crypto qty like 0.001), show up to 6 decimals
      let options;
      if (numeric >= 1) {
        options = { maximumFractionDigits: 5 };
      } else if (numeric >= 0.01) {
        options = { minimumFractionDigits: 2, maximumFractionDigits: 5 };
      } else {
        // For very small quantities like 0.001, 0.0001
        options = { minimumFractionDigits: 3, maximumFractionDigits: 6 };
      }
      return numeric.toLocaleString(undefined, options);
    },
    [parseNumeric]
  );

  // eslint-disable-next-line no-unused-vars
  const formatHistoryDate = useCallback((value) => {
    if (!value) return "--";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString();
  }, []);

  // eslint-disable-next-line no-unused-vars
  const formatSignedCurrency = useCallback(
    (value, { showPlus = false } = {}) => {
      const numeric = Number(value ?? 0);
      const absFormatted = formatCurrency(Math.abs(numeric));
      if (numeric > 0) {
        return `${showPlus ? "+" : ""}$${absFormatted}`;
      }
      if (numeric < 0) {
        return `-$${absFormatted}`;
      }
      return `$${absFormatted}`;
    },
    [formatCurrency]
  );

  const getDisplayQty = useCallback(
    (position) => {
      // First check for grid_lot_size (most reliable source)
      const lotQty = parseNumeric(position?.grid_lot_size);
      if (lotQty !== null) return lotQty;

      // For options, return contracts directly
      if (position?.is_option) {
        const contracts = parseNumeric(position?.qty);
        return contracts;
      }

      const rawQty = parseNumeric(position?.qty);
      if (rawQty === null) return null;

      // Use symbol-specific multiplier (XAUUSD: 100, Crypto: 1)
      const symbol = (position?.symbol || "")
        .toUpperCase()
        .replace(/[/_]/g, "");
      const multiplier = getLotMultiplier(symbol);

      // For crypto (multiplier=1), return qty as-is
      // For XAUUSD (multiplier=100), apply lot conversion
      return rawQty / multiplier;
    },
    [parseNumeric]
  );

  const normalizeClosedPosition = useCallback(
    (position) => {
      const isOption = Boolean(position?.is_option);
      const entryPriceBase = Number(position?.entry_price ?? 0);
      const optionEntry = parseNumeric(position?.option_premium);
      const entryPriceForPnl = isOption
        ? Number(optionEntry ?? entryPriceBase)
        : entryPriceBase;

      const fallbackPrice =
        position?.closed_price ?? position?.current_price ?? entryPriceBase;
      const closedPrice = Number(fallbackPrice ?? entryPriceBase);

      const rawUnits = parseNumeric(position?.qty); // contracts for options; units for FX
      const lotQty = parseNumeric(position?.grid_lot_size);

      // Get symbol-specific multiplier (XAUUSD: 100, Crypto: 1)
      const symbol = (position?.symbol || "")
        .toUpperCase()
        .replace(/[/_]/g, "");
      const multiplier = getLotMultiplier(symbol);

      // Keep qty as display-friendly amount (contracts for options, lots for FX/XAUUSD)
      const displayQty = isOption
        ? rawUnits ?? lotQty // contracts
        : lotQty ?? (rawUnits !== null ? rawUnits / multiplier : null);

      // Effective quantity for PnL math
      const quantityUnits = isOption
        ? rawUnits ?? lotQty ?? 0 // contracts
        : rawUnits ?? (lotQty !== null ? lotQty * multiplier : 0);

      const side = (position?.side || "buy").toLowerCase();
      const priceDiff =
        side === "buy"
          ? closedPrice - entryPriceForPnl
          : entryPriceForPnl - closedPrice;
      const isSPXOption = isOption && (symbol.startsWith("SPX"));
      const effectiveQtyForPnl = isOption
        ? (isSPXOption ? quantityUnits : quantityUnits * OPTION_CONTRACT_MULTIPLIER)
        : quantityUnits;
      const pnlValue = parseNumeric(position?.pnl);
      const derivedPnl = priceDiff * effectiveQtyForPnl;

      return {
        ...position,
        // Preserve original entry price for display; use option_premium only for PnL math
        entry_price: entryPriceBase,
        closed_price: closedPrice,
        qty: quantityUnits,
        display_qty: displayQty,
        pnl: Number.isFinite(pnlValue) ? pnlValue : derivedPnl,
        closed_at: position?.closed_at || position?.last_price_at || null,
      };
    },
    [parseNumeric]
  );

  const evaluateTrend = useCallback((side, entry, current) => {
    if (!Number.isFinite(entry) || !Number.isFinite(current)) return 0;
    const normalizedSide = (side || "buy").toLowerCase();
    const diff = normalizedSide === "sell" ? entry - current : current - entry;
    if (diff > 1e-6) return 1;
    if (diff < -1e-6) return -1;
    return 0;
  }, []);

  const getPriceTrendTextClass = useCallback(
    (position) => {
      const entry = parseNumeric(position.entry_price) ?? 0;
      const current = parseNumeric(position.current_price) ?? entry;
      const side = (position.side ?? "buy").toLowerCase();
      const trend = evaluateTrend(side, entry, current);
      if (trend > 0) return "text-emerald-500";
      if (trend < 0) return "text-red-500";
      return styles.muted;
    },
    [evaluateTrend, parseNumeric, styles.muted]
  );

  const handleLogout = useCallback(() => {
    clearAuth();
    navigate("/");
  }, [navigate]);

  const fetchProfile = useCallback(async () => {
    if (profileRequestRef.current) return;
    profileRequestRef.current = true;
    try {
      const data = await getProfile();
      if (isMountedRef.current) {
        setProfile(data);
      }
    } catch (err) {
      console.error("Failed to fetch profile", err);
      if (err.response?.status === 401) {
        clearAuth();
        navigate("/");
      }
    } finally {
      profileRequestRef.current = false;
    }
  }, [navigate]);

  const fetchPositions = useCallback(async () => {
    if (positionsRequestRef.current) return;
    positionsRequestRef.current = true;
    try {
      const pos = await getPositions();
      if (isMountedRef.current) {
        setPositions(pos);
      }
    } catch (err) {
      console.error("Failed to fetch positions", err);
      if (err.response?.status === 401) {
        clearAuth();
        navigate("/");
      }
    } finally {
      positionsRequestRef.current = false;
    }
  }, [navigate]);

  const fetchHistoryNow = useCallback(
    async (options = {}) => {
      const { silent = false } = options;
      if (historyRequestRef.current) return;

      if (historyFilter === "custom") {
        if (!historyCustom.start || !historyCustom.end) {
          setHistoryFilterError(
            "Select start and end dates to filter history."
          );
          return;
        }
        if (new Date(historyCustom.start) > new Date(historyCustom.end)) {
          setHistoryFilterError("Start date must be before end date.");
          return;
        }
      }

      setHistoryFilterError("");
      historyRequestRef.current = true;
      if (isMountedRef.current) {
        if (silent) {
          setHistoryRefreshing(true);
        } else {
          setHistoryLoaded(false);
          setHistoryLoading(true);
        }
      }

      try {
        const params = {};
        if (historyFilter === "custom") {
          params.start_date = historyCustom.start;
          params.end_date = historyCustom.end;
        } else if (historyFilter) {
          params.period = historyFilter;
        }

        const data = await getUserHistory(params);
        if (isMountedRef.current) {
          setHistoryData({
            transactions: Array.isArray(data.transactions)
              ? data.transactions
              : [],
            closed_positions: Array.isArray(data.closed_positions)
              ? data.closed_positions.map(normalizeClosedPosition)
              : [],
            summary: data.summary || null,
          });
          setHistoryLastUpdated(new Date().toISOString());
          setHistoryLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch history", err);
        if (isMountedRef.current) {
          setHistoryFilterError(
            err?.response?.data?.detail ||
              "Unable to load history for the selected range."
          );
        }
      } finally {
        if (isMountedRef.current) {
          if (silent) {
            setHistoryRefreshing(false);
          } else {
            setHistoryLoading(false);
          }
        }
        historyRequestRef.current = false;
      }
    },
    [
      historyFilter,
      historyCustom.start,
      historyCustom.end,
      normalizeClosedPosition,
    ]
  );

  const fetchHistoryData = useCallback(() => {
    if (historyDebounceRef.current) {
      clearTimeout(historyDebounceRef.current);
    }
    historyDebounceRef.current = setTimeout(() => {
      fetchHistoryNow({ silent: true });
    }, 250);
  }, [fetchHistoryNow]);

  useEffect(() => {
    fetchProfile();
    fetchPositions();
  }, [fetchProfile, fetchPositions]);

  useEffect(() => {
    if (!showHistory) return;
    // Auto-fetch only for preset ranges; for custom, rely on Apply button.
    if (historyFilter === "custom") return;
    fetchHistoryData();
  }, [showHistory, historyFilter, fetchHistoryData]);

  const positionsWsUrl = useMemo(() => {
    const tokenValue = getToken?.();
    if (!tokenValue) return null;
    try {
      const url = new URL(resolveWsUrl("/ws/positions"));
      url.searchParams.set("token", tokenValue);
      return url.toString();
    } catch (error) {
      console.error("Failed to resolve positions websocket URL", error);
      return null;
    }
  }, []);

  // ✅ FIX 5: Removed redundant polling - WebSocket /ws/positions provides real-time updates
  // The positions WebSocket connection below handles all position updates in real-time,
  // so this setInterval polling was causing unnecessary server load and UI flickers.
  // useEffect(() => {
  //   const interval = setInterval(() => {
  //     fetchPositions();
  //   }, REFRESH_INTERVAL_MS);
  //   return () => clearInterval(interval);
  // }, [fetchPositions]);

  useEffect(() => {
    if (!positionsWsUrl) return undefined;

    let reconnectTimer;
    let closedByUser = false;

    const connect = () => {
      try {
        const ws = new WebSocket(positionsWsUrl);
        positionsWsRef.current = ws;

        ws.onopen = () => {
          positionsWsActiveRef.current = true;
        };

        ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data);
            const payload = message?.payload;

            if (message?.type === "snapshot" && Array.isArray(payload)) {
              if (isMountedRef.current) {
                setPositions(payload);
                const closedItems = payload.filter(
                  (item) => (item.status || "").toLowerCase() === "closed"
                );
                if (closedItems.length) {
                  setHistoryData((prev) => {
                    const prevClosed = prev?.closed_positions ?? [];
                    const merged = [...prevClosed];
                    closedItems.forEach((item) => {
                      const normalized = normalizeClosedPosition(item);
                      const idx = merged.findIndex(
                        (existing) =>
                          existing.position_id === normalized.position_id
                      );
                      if (idx >= 0) {
                        merged[idx] = normalized;
                      } else {
                        merged.unshift(normalized);
                      }
                    });
                    return { ...prev, closed_positions: merged };
                  });
                  setHistoryLoaded(true);
                }
              }
              return;
            }

            if (message?.type === "position_update" && payload) {
              setPositions((prev) => {
                if (!payload?.position_id) return prev;
                const status = (payload.status || "").toLowerCase();
                if (status === "closed") {
                  return prev.filter(
                    (item) => item.position_id !== payload.position_id
                  );
                }
                const idx = prev.findIndex(
                  (item) => item.position_id === payload.position_id
                );
                if (idx >= 0) {
                  const next = [...prev];
                  next[idx] = payload;
                  return next;
                }
                return [payload, ...prev];
              });

              if ((payload.status || "").toLowerCase() === "closed") {
                setHistoryData((prev) => {
                  const prevClosed = prev?.closed_positions ?? [];
                  const normalized = normalizeClosedPosition(payload);
                  const idx = prevClosed.findIndex(
                    (item) => item.position_id === normalized.position_id
                  );
                  const merged =
                    idx >= 0
                      ? (() => {
                          const next = [...prevClosed];
                          next[idx] = normalized;
                          return next;
                        })()
                      : [normalized, ...prevClosed];
                  return { ...prev, closed_positions: merged };
                });
                setHistoryLoaded(true);
              }
            }
          } catch (error) {
            console.error(
              "Failed to process positions websocket message",
              error,
              event.data
            );
          }
        };

        ws.onclose = () => {
          positionsWsActiveRef.current = false;
          positionsWsRef.current = null;
          if (!closedByUser) {
            reconnectTimer = window.setTimeout(connect, 5000);
          }
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (error) {
        console.error("Positions websocket connection error", error);
      }
    };

    connect();

    return () => {
      closedByUser = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (positionsWsRef.current) {
        positionsWsRef.current.close();
        positionsWsRef.current = null;
      }
      positionsWsActiveRef.current = false;
    };
  }, [positionsWsUrl, normalizeClosedPosition]);

  useEffect(() => {
    if (!showHistory) return undefined;
    fetchHistoryData();
    const interval = setInterval(() => {
      fetchHistoryData();
    }, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [showHistory, fetchHistoryData]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchProfile();
        fetchPositions();
        if (showHistory) {
          fetchHistoryData();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    window.addEventListener("focus", handleVisibility);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("focus", handleVisibility);
    };
  }, [fetchProfile, fetchPositions, fetchHistoryData, showHistory]);

  useEffect(() => {
    let reconnectTimer;
    let closedByUser = false;

    const connect = () => {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          const identifiers = [data.label, data.symbol, data.oandaInstrument]
            .filter(Boolean)
            .map((id) => String(id));
          const midValue =
            typeof data.mid === "number"
              ? data.mid
              : typeof data.price === "number"
              ? data.price
              : null;

          setPrices((prev) => {
            const next = { ...prev };
            identifiers.forEach((id) => {
              const existing = prev[id] || {};
              const baseMid = existing.baseMid ?? midValue;
              let change = existing.change || 0;
              if (baseMid && midValue) {
                change = ((midValue - baseMid) / baseMid) * 100;
              }

              const previousBid = existing.bid;
              const previousAsk = existing.ask;
              const nextBid = typeof data.bid === "number" ? data.bid : null;
              const nextAsk = typeof data.ask === "number" ? data.ask : null;

              let bidDirection = existing.bidDirection || "flat";
              if (nextBid !== null && previousBid !== null) {
                if (nextBid > previousBid) bidDirection = "up";
                else if (nextBid < previousBid) bidDirection = "down";
              }

              let askDirection = existing.askDirection || "flat";
              if (nextAsk !== null && previousAsk !== null) {
                if (nextAsk > previousAsk) askDirection = "up";
                else if (nextAsk < previousAsk) askDirection = "down";
              }

              next[id] = {
                bid: nextBid,
                ask: nextAsk,
                mid: midValue,
                change,
                baseMid: baseMid || midValue,
                bidDirection,
                askDirection,
              };
            });
            return next;
          });
        } catch (error) {
          console.error("Failed to parse price update", error, event.data);
        }
      };

      ws.onclose = () => {
        if (!closedByUser) {
          reconnectTimer = setTimeout(connect, 5000);
        }
      };

      ws.onerror = () => {
        ws.close();
      };
    };

    connect();

    return () => {
      closedByUser = true;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
      }
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [wsUrl]);

  useEffect(() => {
    if (showHistory && !historyLoaded && !historyLoading) {
      fetchHistoryData();
    }
  }, [showHistory, historyLoaded, historyLoading, fetchHistoryData]);

  useEffect(() => {
    if (activeTab === "trade" && !historyLoaded && !historyLoading) {
      fetchHistoryData();
    }
  }, [activeTab, historyLoaded, historyLoading, fetchHistoryData]);

  useEffect(() => {
    if (!balanceNotice) return undefined;
    const timer = setTimeout(() => setBalanceNotice(null), 6000);
    return () => clearTimeout(timer);
  }, [balanceNotice]);

  const renderQuotes = () => (
    <div className="space-y-3">
      {SYMBOLS.map(({ label, symbol, display }) => {
        const info = prices[label] || prices[symbol] || {};
        const bidDirection = info.bidDirection;
        const askDirection = info.askDirection;
        const bidColor =
          bidDirection === "up"
            ? "text-emerald-500"
            : bidDirection === "down"
            ? "text-red-500"
            : "";
        const askColor =
          askDirection === "up"
            ? "text-emerald-500"
            : askDirection === "down"
            ? "text-red-500"
            : "";
        const bid =
          typeof info.bid === "number"
            ? formatPrice(symbol, info.bid)
            : typeof info.mid === "number"
            ? formatPrice(symbol, info.mid)
            : "--";
        const ask =
          typeof info.ask === "number"
            ? formatPrice(symbol, info.ask)
            : typeof info.mid === "number"
            ? formatPrice(symbol, info.mid)
            : "--";
        const change = info.change || 0;
        const changeColor = change >= 0 ? "text-emerald-500" : "text-red-500";
        const changeLabel = info.mid
          ? `${change >= 0 ? "+" : ""}${change.toFixed(2)}%`
          : "--";

        return (
          <div
            key={symbol}
            className={`rounded-2xl px-4 py-3 flex flex-col gap-2 shadow-sm ${styles.card}`}
          >
            <div className="flex items-center justify-between">
              <span className="font-semibold text-base tracking-wide">
                {display}
              </span>
              <span className={`text-xs font-medium ${changeColor}`}>
                {changeLabel}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <div className="flex flex-col">
                <span className="uppercase text-xs opacity-60">Bid</span>
                <span className={`font-mono text-lg ${bidColor}`}>{bid}</span>
              </div>
              <div className="flex flex-col items-end">
                <span className="uppercase text-xs opacity-60">Ask</span>
                <span className={`font-mono text-lg ${askColor}`}>{ask}</span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );

  const formatTimestamp = useCallback((isoValue) => {
    if (!isoValue) return "--";
    const normalizedIso = /[zZ]|[+-]\d\d:\d\d$/.test(isoValue)
      ? isoValue
      : `${isoValue}Z`;
    const date = new Date(normalizedIso);
    if (Number.isNaN(date.getTime())) return isoValue;
    return `${date.toISOString().replace("T", " ").replace("Z", "")} UTC`;
  }, []);

  const renderOpenPositionsSection = () => {
    const summaryReady = historyLoaded && !historyLoading;
    const baseBalanceValue = summaryReady
      ? closedSummary.balance
      : totals.balance;
    const equityValue = baseBalanceValue + totals.pnl;

    return (
      <div className="space-y-3">
        <div className={`rounded-2xl px-4 py-4 ${styles.card}`}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="flex flex-col">
              <span className="uppercase text-xs opacity-60">Balance</span>
              <span className="text-lg font-semibold">
                ${formatCurrency(baseBalanceValue)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="uppercase text-xs opacity-60">Equity</span>
              <span className="text-lg font-semibold">
                ${formatCurrency(equityValue)}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="uppercase text-xs opacity-60">Open P&L</span>
              <span
                className={`text-lg font-semibold ${
                  totals.pnl >= 0 ? "text-emerald-500" : "text-red-500"
                }`}
              >
                ${formatCurrency(totals.pnl)}
              </span>
            </div>
          </div>
        </div>

        {positions.length === 0 ? (
          <div className={`rounded-2xl px-4 py-6 text-center ${styles.card}`}>
            <p className="text-sm">No open positions yet.</p>
            <p className={`text-xs mt-1 ${styles.muted}`}>
              Positions created by the admin will appear here in real time.
            </p>
          </div>
        ) : (
          positions.map((pos) => {
            const isBuy = pos.side === "buy";
            const displayQty = getDisplayQty(pos);
            const quantityLabel = formatQuantity(displayQty);
            return (
              <div
                key={pos.position_id}
                className={`rounded-2xl px-4 py-3 shadow-sm border-l-4 ${
                  isBuy ? "border-emerald-500" : "border-red-500"
                } ${styles.card}`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm font-semibold">
                    {pos.display_symbol || pos.symbol}
                  </span>
                  <span
                    className={`text-sm font-semibold ${
                      pos.unrealized_pnl >= 0
                        ? "text-emerald-500"
                        : "text-red-500"
                    }`}
                  >
                    {pos.unrealized_pnl?.toFixed(2)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs mt-2">
                  <span>
                    {isBuy ? "BUY" : "SELL"} @{" "}
                    {formatPrice(pos.symbol, pos.entry_price)}
                  </span>
                  <span>Qty: {quantityLabel}</span>
                </div>
                <div className={`text-xs mt-1 ${getPriceTrendTextClass(pos)}`}>
                  Current: {formatPrice(pos.symbol, pos.current_price)}
                </div>
                <div className="text-xs text-gray-400 mt-1">
                  Swap accrued: ${Number(pos.swap_accrued || 0).toFixed(2)}
                  {pos.last_swap_at
                    ? ` (last ${new Date(
                        pos.last_swap_at
                      ).toLocaleDateString()})`
                    : ""}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  const renderHistorySection = () => (
    <div className="space-y-4">
      {/* Filter Bar */}
      <div className={`rounded-2xl px-4 py-4 shadow-sm ${styles.card}`}>
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            {HISTORY_FILTER_PRESETS.map((preset) => (
              <button
                key={preset.id}
                className={`px-3 py-1 rounded-full text-sm border transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                  historyFilter === preset.id
                    ? "bg-emerald-500 text-white border-emerald-500"
                    : `${styles.navInactive} ${styles.navBorder} border hover:border-emerald-500`
                }`}
                onClick={() => handleHistoryFilterChange(preset.id)}
                disabled={historyLoading}
              >
                {preset.label}
              </button>
            ))}
          </div>
          {historyFilter === "custom" && (
            <div className="flex items-end gap-3">
              <div className="flex flex-col">
                <label className="text-xs opacity-60 mb-1">Start</label>
                <input
                  type="date"
                  className={`px-3 py-2 rounded-md border ${styles.navBorder} bg-transparent disabled:opacity-60`}
                  value={historyCustom.start}
                  onChange={(e) =>
                    handleHistoryDateChange("start", e.target.value)
                  }
                  disabled={historyLoading}
                />
              </div>
              <div className="flex flex-col">
                <label className="text-xs opacity-60 mb-1">End</label>
                <input
                  type="date"
                  className={`px-3 py-2 rounded-md border ${styles.navBorder} bg-transparent disabled:opacity-60`}
                  value={historyCustom.end}
                  onChange={(e) =>
                    handleHistoryDateChange("end", e.target.value)
                  }
                  disabled={historyLoading}
                />
              </div>
              <button
                className="px-3 py-2 rounded-md text-sm bg-emerald-500 text-white disabled:opacity-60 disabled:cursor-not-allowed"
                onClick={() => {
                  // Cancel pending debounce and fetch immediately
                  if (historyDebounceRef.current) {
                    clearTimeout(historyDebounceRef.current);
                    historyDebounceRef.current = null;
                  }
                  fetchHistoryNow();
                }}
                disabled={
                  historyLoading ||
                  (historyFilter === "custom" &&
                    (!historyCustom.start || !historyCustom.end))
                }
              >
                {historyLoading ? "Applying..." : "Apply"}
              </button>
            </div>
          )}
        </div>
        {historyFilterError && (
          <div className="mt-2 text-sm text-red-500">{historyFilterError}</div>
        )}
        {historyLastUpdated && (
          <div className={`mt-2 text-xs ${styles.muted}`}>
            Last updated: {new Date(historyLastUpdated).toLocaleString()}
          </div>
        )}
      </div>
      {/* History Summary with stable height to avoid flicker */}
      <div
        className={`rounded-2xl px-4 py-4 shadow-sm ${styles.card}`}
        style={{ minHeight: 110 }}
      >
        <div className="flex items-center justify-between mb-2">
          <p className="text-sm font-semibold">History Summary</p>
          {(historyLoading || historyRefreshing) && (
            <span className={`text-[11px] ${styles.muted}`}>Updating...</span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Closed PnL</span>
            <span
              className={`text-lg font-semibold ${
                closedSummary.profit >= 0 ? "text-emerald-500" : "text-red-500"
              }`}
            >
              ${formatCurrency(closedSummary.profit)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Deposits</span>
            <span className="text-lg font-semibold">
              ${formatCurrency(closedSummary.depositsTotal)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">Withdrawals</span>
            <span className="text-lg font-semibold">
              ${formatCurrency(closedSummary.withdrawalsTotal)}
            </span>
          </div>
          <div className="flex flex-col">
            <span className="uppercase text-xs opacity-60">
              Balance (period end)
            </span>
            <span className="text-lg font-semibold">
              ${formatCurrency(closedSummary.balance)}
            </span>
          </div>
        </div>
      </div>
      <div>
        <p className="uppercase text-xs opacity-60 mb-2">Transactions</p>
        <div
          className={`rounded-2xl px-4 py-4 shadow-sm ${styles.card}`}
          style={{ minHeight: 120 }}
        >
          {historyData.transactions.length === 0 ? (
            <p className={`text-xs ${styles.muted}`}>No transactions yet.</p>
          ) : (
            <div className="space-y-3">
              {historyData.transactions.map((tx) => {
                const classification = classifyTransaction(tx);
                const amountValue = Number(tx?.amount ?? 0);
                const amountAbs = Math.abs(amountValue);
                const isDeposit = classification === "deposit";
                const isWithdraw = classification === "withdraw";
                const borderColor = isDeposit
                  ? "border-emerald-500"
                  : isWithdraw
                  ? "border-red-500"
                  : "border-slate-500";
                const amountClass = isDeposit
                  ? "text-emerald-500"
                  : isWithdraw
                  ? "text-red-500"
                  : styles.muted;
                const amountLabel = `${
                  isWithdraw ? "-" : isDeposit ? "+" : ""
                }$${formatCurrency(amountAbs)}`;
                return (
                  <div
                    key={tx.transaction_id}
                    className={`rounded-2xl px-4 py-3 shadow-sm border-l-4 ${borderColor} ${styles.card}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold capitalize">
                          {tx.action || "Transaction"}
                        </p>
                        <p className={`text-[11px] ${styles.muted}`}>
                          {formatTimestamp(tx.created_at)}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`text-sm font-semibold ${amountClass}`}>
                          {amountLabel}
                        </p>
                        {tx.balance_after !== null &&
                          tx.balance_after !== undefined && (
                            <p className={`text-[11px] ${styles.muted}`}>
                              Balance: ${formatCurrency(tx.balance_after)}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div>
        <p className="uppercase text-xs opacity-60 mb-2">Closed Positions</p>
        {historyLoading ? (
          <div className={`rounded-2xl px-4 py-6 text-center ${styles.card}`}>
            <p className={`text-xs ${styles.muted}`}>Loading history...</p>
          </div>
        ) : historyData.closed_positions.length === 0 ? (
          <div className={`rounded-2xl px-4 py-6 text-center ${styles.card}`}>
            <p className={`text-xs ${styles.muted}`}>
              No closed positions yet.
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {historyData.closed_positions.map((pos) => {
              const entryPrice = Number(pos.entry_price ?? 0);
              const fallbackPrice = pos.current_price ?? pos.entry_price;
              const closedPriceRaw = pos.closed_price ?? fallbackPrice;
              const closedPrice = Number(closedPriceRaw ?? entryPrice);
              const qtyRaw = parseNumeric(pos.qty);
              const gridQtyRaw = parseNumeric(pos.grid_lot_size);
              const isOption = Boolean(pos.is_option);
              const isSPXOption = pos.option_metadata != null || (pos.asset_type === "options" && (pos.symbol || "").startsWith("SPX"));
              // Get symbol-specific multiplier (XAUUSD: 100, Crypto: 1)
              const symbolNorm = (pos.symbol || "")
                .toUpperCase()
                .replace(/[/_]/g, "");
              const multiplier = getLotMultiplier(symbolNorm);

              const qtyUnits = isSPXOption
                ? (qtyRaw ?? 0)
                : isOption
                ? (qtyRaw ?? 0) * OPTION_CONTRACT_MULTIPLIER
                : qtyRaw ?? (gridQtyRaw !== null ? gridQtyRaw * multiplier : 0);
              const displayQty = isSPXOption
                ? gridQtyRaw ?? (qtyRaw !== null ? qtyRaw / 100 : null)
                : isOption
                ? qtyRaw
                : gridQtyRaw ?? (qtyRaw !== null ? qtyRaw / multiplier : null);
              const isBuy = (pos.side || "buy").toLowerCase() === "buy";
              const derivedPnl = isBuy
                ? (closedPrice - entryPrice) * qtyUnits
                : (entryPrice - closedPrice) * qtyUnits;
              const closedPnl = parseNumeric(pos.pnl);
              const displayClosedPnl = Number.isFinite(closedPnl)
                ? closedPnl
                : derivedPnl;
              const borderColor =
                displayClosedPnl >= 0 ? "border-emerald-500" : "border-red-500";

              // SPX Options: custom display
              const optMeta = pos.option_metadata;
              const displaySymbol = isSPXOption
                ? `SPX ${(optMeta?.option_type || pos.option_type || "").toUpperCase()} ${(pos.side || "").toUpperCase()}`
                : (pos.display_symbol || pos.symbol);

              return (
                <div
                  key={pos.position_id}
                  className={`rounded-2xl px-4 py-3 shadow-sm border-l-4 ${borderColor} ${styles.card}`}
                >
                  <div className="flex items-center justify-between text-sm font-semibold">
                    <span>{displaySymbol}</span>
                    <span
                      className={
                        displayClosedPnl >= 0
                          ? "text-emerald-500"
                          : "text-red-500"
                      }
                    >
                      {displayClosedPnl.toFixed(2)}
                    </span>
                  </div>
                  {isSPXOption ? (
                    <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                      <span>Strike: {optMeta?.strike_price ?? pos.strike_price ?? "—"}</span>
                      <span>Expiry: {optMeta?.expiry_date ?? pos.expiry_date ?? "—"}</span>
                      <span>Entry Prem: ${entryPrice.toFixed(2)}</span>
                      <span>Exit Prem: ${closedPrice.toFixed(2)}</span>
                      <span>Lots: {optMeta?.lots ?? displayQty ?? "—"}</span>
                      <span>Contracts: {Number(qtyRaw ?? 0).toFixed(0)}</span>
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                      <span>Entry: {formatPrice(pos.symbol, entryPrice)}</span>
                      <span>Closed: {formatPrice(pos.symbol, closedPrice)}</span>
                      <span>Qty: {formatQuantity(displayQty)}</span>
                      <span>Status: {pos.status || "closed"}</span>
                    </div>
                  )}
                  {!isSPXOption && (
                    <div className="text-[11px] text-gray-400 mt-1">
                      Swap accrued: ${Number(pos.swap_accrued || 0).toFixed(2)}
                      {pos.last_swap_at
                        ? ` (last ${new Date(
                            pos.last_swap_at
                          ).toLocaleDateString()})`
                        : ""}
                    </div>
                  )}
                  <p className={`text-[11px] mt-2 ${styles.muted}`}>
                    Closed on {formatTimestamp(pos.closed_at)}
                  </p>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderTrade = () => {
    return (
      <div className="space-y-4">
        <div className="flex gap-2">
          <button
            className={`flex-1 rounded-full py-2 font-semibold ${
              showHistory ? styles.card : "bg-emerald-600 text-white"
            }`}
            onClick={handleShowOpenPositions}
          >
            Open Positions ({positions.length})
          </button>
          <button
            className={`flex-1 rounded-full py-2 font-semibold ${
              showHistory ? "bg-emerald-600 text-white" : styles.card
            }`}
            onClick={handleShowHistory}
          >
            History
          </button>
        </div>

        {showHistory ? renderHistorySection() : renderOpenPositionsSection()}
      </div>
    );
  };

  const renderProfile = () => (
    <div className="flex flex-col gap-4">
      {balanceNotice && (
        <div
          className={`rounded-2xl px-4 py-3 text-sm border ${
            balanceNotice.type === "error"
              ? "border-red-400/60 bg-red-500/10 text-red-600 dark:text-red-300"
              : "border-emerald-400/60 bg-emerald-500/10 text-emerald-600 dark:text-emerald-300"
          }`}
        >
          {balanceNotice.message}
        </div>
      )}
      <div
        className={`rounded-2xl px-6 py-8 flex flex-col items-center gap-3 ${styles.card}`}
      >
        <div className="w-20 h-20 rounded-full bg-emerald-600 text-white flex items-center justify-center text-3xl">
          {profile.username
            ? profile.username
                .split(" ")
                .map((part) => part[0])
                .join("")
                .slice(0, 2)
                .toUpperCase()
            : storedUsername
            ? storedUsername[0].toUpperCase()
            : "?"}
        </div>
        <div className="text-center">
          <p className="text-lg font-semibold">
            {profile.username || storedUsername || "Trader"}
          </p>
          <p className={`text-xs ${styles.muted}`}>
            Account ID: {profile.account_id || "--"}
          </p>
          <p className={`text-xs ${styles.muted}`}>
            {profile.email || storedEmail || ""}
          </p>
        </div>
      </div>

      <div className={`rounded-2xl overflow-hidden ${styles.card}`}>
        <button
          className="w-full text-left px-5 py-4 text-sm font-medium flex items-center justify-between border-b border-current/10"
          onClick={() =>
            setBalanceModal({ open: true, type: "add", amount: "" })
          }
        >
          <span>Add Balance</span>
          <span className="opacity-50">›</span>
        </button>
        <button
          className="w-full text-left px-5 py-4 text-sm font-medium flex items-center justify-between border-b border-current/10"
          onClick={() =>
            setBalanceModal({ open: true, type: "withdraw", amount: "" })
          }
        >
          <span>Withdraw Balance</span>
          <span className="opacity-50">›</span>
        </button>
        <button className="w-full text-left px-5 py-4 text-sm font-medium flex items-center justify-between border-b border-current/10">
          <span>Settings</span>
          <span className="opacity-50">›</span>
        </button>
      </div>

      <button
        onClick={handleLogout}
        className="mt-2 rounded-full bg-red-600 text-white font-semibold py-3"
      >
        Logout
      </button>
    </div>
  );

  const handleBalanceSubmit = async () => {
    const amountValue = parseFloat(balanceModal.amount);
    if (Number.isNaN(amountValue) || amountValue <= 0) {
      setBalanceError("Enter a valid amount greater than zero");
      return;
    }
    try {
      setBalanceError("");
      const requestType = balanceModal.type === "add" ? "deposit" : "withdraw";
      const res = await requestBalanceChange(requestType, amountValue);
      const fallbackMessage =
        requestType === "deposit"
          ? "Deposit request submitted. We'll update you once it is processed."
          : "Withdrawal request submitted. We'll update you once it is processed.";
      setBalanceNotice({
        type: "info",
        message: res?.message || fallbackMessage,
      });
      setBalanceModal({ open: false, type: balanceModal.type, amount: "" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      setBalanceError(detail || "Unable to update balance");
      if (error.response?.status === 401) {
        setBalanceNotice({
          type: "error",
          message: "Session expired. Please log in again.",
        });
        setTimeout(() => {
          handleLogout();
        }, 1000);
      } else {
        setBalanceNotice({
          type: "error",
          message: detail || "Balance request failed. Please try again.",
        });
      }
    }
  };

  const handleShowOpenPositions = useCallback(() => {
    setShowHistory(false);
  }, []);

  const handleShowHistory = useCallback(() => {
    setShowHistory(true);
    setHistoryFilterError("");
  }, []);

  const handleHistoryFilterChange = useCallback((filterId) => {
    setHistoryFilter(filterId);
    setHistoryFilterError("");
    if (filterId !== "custom") {
      setHistoryCustom({ start: "", end: "" });
    }
  }, []);

  const handleHistoryDateChange = useCallback((field, value) => {
    setHistoryCustom((prev) => ({ ...prev, [field]: value }));
  }, []);

  const renderBalanceModal = () => {
    if (!balanceModal.open) return null;
    const isAdd = balanceModal.type === "add";
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
        <div
          className={`w-full max-w-sm rounded-2xl p-6 space-y-4 ${styles.surface}`}
        >
          <div>
            <h2 className="text-lg font-semibold text-center">
              {isAdd ? "Add Balance" : "Withdraw Balance"}
            </h2>
            <p className={`text-xs text-center ${styles.muted}`}>
              Current Balance: ${balanceDisplay}
            </p>
          </div>
          <input
            type="number"
            step="0.01"
            className="w-full rounded-full border border-gray-300 bg-white/10 px-4 py-3 text-center text-base outline-none focus:ring-2 focus:ring-emerald-500"
            placeholder="Enter amount"
            value={balanceModal.amount}
            onChange={(e) =>
              setBalanceModal((prev) => ({ ...prev, amount: e.target.value }))
            }
          />
          {isAdd && (
            <div className="rounded-xl border border-emerald-500/40 px-4 py-3 text-sm">
              <p className="font-semibold text-emerald-500">
                Send USDT (BEP-20)
              </p>
              <p className={`mt-1 text-xs ${styles.muted}`}>
                Transfer your deposit to the wallet below, then submit this
                request so we can verify and credit the funds.
              </p>
              <div className="mt-3 flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 px-3 py-2">
                <code className="flex-1 break-all text-[11px]">
                  {USDT_WALLET_ADDRESS}
                </code>
                <button
                  type="button"
                  onClick={handleCopyDepositAddress}
                  className="rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white"
                >
                  Copy
                </button>
              </div>
              <p className={`mt-2 text-[11px] ${styles.muted}`}>
                Network: Binance Smart Chain (BEP-20)
              </p>
            </div>
          )}
          {balanceError && (
            <div className="text-xs text-red-500 text-center">
              {balanceError}
            </div>
          )}
          <div className="flex gap-3">
            <button
              className="flex-1 rounded-full bg-emerald-600 text-white py-2 font-semibold"
              onClick={handleBalanceSubmit}
            >
              Confirm
            </button>
            <button
              className={`flex-1 rounded-full py-2 font-semibold ${styles.card}`}
              onClick={() =>
                setBalanceModal({
                  open: false,
                  type: balanceModal.type,
                  amount: "",
                })
              }
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "quotes":
        return renderQuotes();
      case "trade":
        return renderTrade();
      case "profile":
        return renderProfile();
      default:
        return null;
    }
  };

  return (
    <div className={`min-h-screen flex justify-center ${styles.viewport}`}>
      <div className={`mobile-frame flex flex-col ${styles.surface}`}>
        <header className="px-5 py-4 flex items-center justify-between border-b border-white/10">
          <div className="flex flex-col">
            <span className="text-sm uppercase tracking-widest opacity-60">
              AstraTrade
            </span>
            <span className="text-base font-semibold">
              Hi, {profile.username || storedUsername || "Trader"}
            </span>
            <span className={`text-[10px] ${styles.muted}`}>
              Account ID: {profile.account_id || "--"}
            </span>
          </div>
          <button
            className="rounded-full border border-current px-3 py-1 text-sm"
            onClick={toggleTheme}
          >
            {theme === "dark" ? "☀️" : "🌙"}
          </button>
        </header>

        <main className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {renderContent()}
        </main>

        <nav
          className={`grid grid-cols-3 text-sm border-t ${styles.navBorder} py-2 bg-white/5 backdrop-blur`}
        >
          {TABS.map((tab) => {
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex flex-col items-center gap-1 py-1 transition-colors ${
                  active ? styles.navActive : styles.navInactive
                }`}
              >
                <span>{tab.icon}</span>
                <span className="text-xs font-medium">{tab.label}</span>
              </button>
            );
          })}
        </nav>
        {renderBalanceModal()}
      </div>
    </div>
  );
}
